
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAdminListingMutations } from "@/hooks/useAdminListings";
import { AdminListing, ListingStatus } from "@/lib/types/adminTypes";
import { Table, TableBody } from "@/components/ui/table";
import { Loader } from "lucide-react";
import { Pagination } from "@/components/ui/pagination";

// Import the extracted components
import { BulkActionsBar } from "./table/BulkActionsBar";
import { DeleteConfirmationDialog } from "./dialogs/DeleteConfirmationDialog";
import { StatusChangeDialog } from "./dialogs/StatusChangeDialog";
import { ListingTableRow } from "./table/ListingTableRow";
import { ListingsTableHeader } from "./table/ListingsTableHeader";
import { ListingsEmptyState } from "./table/ListingsEmptyState";

interface AdminListingsTableProps {
  listings: AdminListing[];
  isLoading: boolean;
  pagination?: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
  };
  onPageChange: (page: number) => void;
  showPagination?: boolean;
  limit?: number;
}

export function AdminListingsTable({ 
  listings, 
  isLoading, 
  pagination, 
  onPageChange, 
  showPagination = true,
  limit = 10
}: AdminListingsTableProps) {
  const navigate = useNavigate();
  const { deleteListing, bulkDeleteListings, changeStatus, bulkUpdateStatus } = useAdminListingMutations();
  
  const [selectedListings, setSelectedListings] = useState<string[]>([]);
  const [listingToDelete, setListingToDelete] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusToChange, setStatusToChange] = useState<ListingStatus | null>(null);
  const [statusChangeReason, setStatusChangeReason] = useState<string>("");
  
  const handleSelectAll = () => {
    if (selectedListings.length === listings.length) {
      setSelectedListings([]);
    } else {
      setSelectedListings(listings.map(listing => listing.id.toString()));
    }
  };
  
  const handleSelectListing = (id: string) => {
    setSelectedListings(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };
  
  // DELETE - Admin API endpoint: DELETE /api/admin/listings/{id}
  const handleDeleteConfirmed = async () => {
    if (listingToDelete) {
      await deleteListing.mutateAsync(listingToDelete);
      setListingToDelete(null);
      setShowDeleteDialog(false);
    }
  };
  
  // DELETE (bulk) - Admin API endpoint: via adminService.bulkDeleteListings
  const handleBulkDeleteConfirmed = async () => {
    await bulkDeleteListings.mutateAsync(selectedListings);
    setSelectedListings([]);
    setShowBulkDeleteDialog(false);
  };
  
  // PATCH - Admin API endpoint: PATCH /api/admin/listings/{id}/status
  const handleStatusChangeConfirmed = async () => {
    if (statusToChange && listingToDelete) {
      await changeStatus.mutateAsync({ 
        id: listingToDelete,
        statusData: { 
          status: statusToChange,
          reason: statusChangeReason.trim() || undefined
        }
      });
      setListingToDelete(null);
      setStatusToChange(null);
      setStatusChangeReason("");
      setShowStatusDialog(false);
    }
  };
  
  // PATCH (bulk) - Admin API endpoint: via adminService.bulkUpdateStatus
  const handleBulkStatusChange = async (status: ListingStatus) => {
    await bulkUpdateStatus.mutateAsync({
      ids: selectedListings,
      statusData: { 
        status,
        reason: "Bulk status update" 
      }
    });
    setSelectedListings([]);
  };
  
  // GET - View listing details (not an admin-specific endpoint)
  const handleViewListing = (id: string) => {
    navigate(`/property/${id}`);
  };
  
  // GET then PUT - Admin API endpoints: GET /api/admin/listings/{id} followed by potential PUT /api/admin/listings/{id}
  const handleEditListing = (id: string) => {
    navigate(`/admin/listings/${id}/edit`);
  };
  
  // Open the status change dialog - PATCH /api/admin/listings/{id}/status
  const handleStatusChange = (id: string, status: ListingStatus) => {
    setListingToDelete(id);
    setStatusToChange(status);
    setShowStatusDialog(true);
  };

  // Open delete confirmation - DELETE /api/admin/listings/{id}
  const handleDeleteListing = (id: string) => {
    setListingToDelete(id);
    setShowDeleteDialog(true);
  };

  // Open bulk delete confirmation
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!listings || listings.length === 0) {
    return (
      <div className="p-8 text-center border rounded-md">
        <p className="text-muted-foreground">No listings found matching your criteria</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectedListings.length > 0 && (
        <BulkActionsBar 
          selectedCount={selectedListings.length}
          onBulkStatusChange={handleBulkStatusChange}
          onBulkDelete={handleBulkDelete}
        />
      )}
      
      <div className="rounded-md border">
        <Table>
          <ListingsTableHeader 
            onSelectAll={handleSelectAll}
            allSelected={listings.length > 0 && selectedListings.length === listings.length}
            hasListings={listings.length > 0}
          />
          
          <TableBody>
            {listings.length > 0 ? (
              listings.map((listing: AdminListing) => (
                <ListingTableRow
                  key={listing.id}
                  listing={listing}
                  isSelected={selectedListings.includes(listing.id.toString())}
                  onSelect={handleSelectListing}
                  onViewListing={handleViewListing}
                  onEditListing={handleEditListing}
                  onDeleteListing={handleDeleteListing}
                  onStatusChange={handleStatusChange}
                />
              ))
            ) : (
              <ListingsEmptyState />
            )}
          </TableBody>
        </Table>
      </div>
      
      {pagination && showPagination && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={onPageChange}
          isLoading={isLoading}
        />
      )}
      
      {/* Confirmation Dialogs */}
      <DeleteConfirmationDialog 
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteConfirmed}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the listing and remove it from our servers."
      />
      
      <DeleteConfirmationDialog 
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
        onConfirm={handleBulkDeleteConfirmed}
        title={`Delete ${selectedListings.length} listings?`}
        description={`This action cannot be undone. This will permanently delete ${
          selectedListings.length === 1 ? "this listing" : `these ${selectedListings.length} listings`
        } and remove them from our servers.`}
      />
      
      <StatusChangeDialog 
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        onConfirm={handleStatusChangeConfirmed}
        status={statusToChange}
      />
    </div>
  );
}
